import { Box, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwCumulativeGraphBySchoolGroup } from './RwwCumulativeGraphBySchoolGroup';
import {
  KEY_CUMULATIVE,
  KEY_PORTAL_MONTHLY_SCHOOL_GROUP_READ_STAT_LIST,
  KEY_PORTAL_MONTHLY_SCHOOL_GROUP_WALK_STAT_LIST,
  KEY_PORTAL_MONTHLY_SCHOOL_GROUP_WRITE_STAT_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  downloadPortalSchoolGroupMonthlyDataList,
  getPortalMonthlySchoolGroupReadStatList,
  getPortalMonthlySchoolGroupWalkStatList,
  getPortalMonthlySchoolGroupWriteStatList,
} from '../../common/api/ApiPortal';

interface Props {
  title: string;
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwCumulativeStatisticsBySchoolGroup: React.FC<Props> = ({
  title,
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchPortalMonthlySchoolGroupReadStat,
  } = useQuery(
    KEY_PORTAL_MONTHLY_SCHOOL_GROUP_READ_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlySchoolGroupReadStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalMonthlySchoolGroupWalkStat } = useQuery(
    KEY_PORTAL_MONTHLY_SCHOOL_GROUP_WALK_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlySchoolGroupWalkStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchPortalMonthlySchoolGroupWriteStat } = useQuery(
    KEY_PORTAL_MONTHLY_SCHOOL_GROUP_WRITE_STAT_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getPortalMonthlySchoolGroupWriteStatList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadPortalSchoolGroupMonthlyData = () => {
    const newData: any = {
      type: getTypeWord(statType),
      dataType: KEY_CUMULATIVE,
      year: getYear(startDate * 1000),
      month: getMonthText(startDate * 1000),
    };
    downloadPortalSchoolGroupMonthlyDataList(newData);
  };

  useEffect(() => {
    if (reqRequest) {
      refetchPortalMonthlySchoolGroupReadStat();
    }
  }, [startDate]);

  useEffect(() => {
    if (reqRequest) {
      refetchPortalMonthlySchoolGroupReadStat();
    }
  }, [reqRequest]);

  useEffect(() => {
    setReqRequest(true);
  }, []);

  return (
    <>
      <Paper>
        <SwTitleDownloadMore title={title} />
        <SwRwwTab
          onChangeTab={value => {
            setStatType(
              value === 0
                ? KEY_READ_TEXT
                : value === 1
                ? KEY_WALK_TEXT
                : KEY_WRITE_TEXT
            );
            value === 0
              ? refetchPortalMonthlySchoolGroupReadStat()
              : value === 1
              ? refetchPortalMonthlySchoolGroupWalkStat()
              : refetchPortalMonthlySchoolGroupWriteStat();
          }}
          onDownload={() => downloadPortalSchoolGroupMonthlyData()}
        />
        <RwwCumulativeGraphBySchoolGroup
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.rate}
          isPdf={false}
        />
      </Paper>
    </>
  );
};
